import { useRef } from "react";
import { Link } from "react-router-dom";
import { motion, useScroll, useTransform } from "framer-motion";
import { ArrowRight, MessageCircle, Check, Star, Quote, ArrowUpRight, Info } from "lucide-react";
import { MaskedLines, Reveal, Marquee } from "../components/Reveal";
import { SectionHeading, ProcessSection, CTABand, FlagDot, usePageTitle } from "../components/Shared";
import AppCta from "../components/AppCta";
import ConcoursBanner from "../components/ConcoursBanner";
import { Accordion, AccordionContent, AccordionItem, AccordionTrigger } from "@/components/ui/accordion";
import { IMAGES, WHATSAPP_URL } from "../data/content";
import { useLang, useContent } from "../i18n";

const COPY = {
  fr: {
    badge: "Besoin d'effectuer une démarche au Togo ?",
    sub1: "Que vous soyez au Togo ou à l'étranger, DOC TOGO vous accompagne dans vos démarches administratives, la création d'entreprise et vos projets d'installation au Togo.",
    sub2: "Un besoin. Une démarche. Un accompagnement.",
    disclaimer: <>DOC TOGO est une plateforme privée d'accompagnement : <strong className="font-semibold text-white/80">nous ne sommes pas l'administration togolaise et nous ne la remplaçons pas.</strong> Nous fournissons l'information et l'assistance nécessaires à la personne demanderesse, du dépôt à la remise du document.</>,
    stats: [
      { value: "99,4%", label: "de dossiers aboutis" },
      { value: "+1 500", label: "démarches accompagnées" },
      { value: "24h", label: "de délai de réponse" },
    ],
    trust1: "Diaspora & investisseurs",
    trust2: "nous font confiance dans 20+ pays",
    servicesEyebrow: "Nos services",
    servicesSub: "Cinq pôles d'expertise pour les particuliers, la diaspora, les étrangers et les entreprises.",
    allServices: "Voir tous nos services",
    custom: "Sur mesure",
    customTitle: "Votre démarche n'apparaît pas ?",
    customText: "Décrivez-nous votre besoin : nous étudions toutes les demandes, même les plus spécifiques.",
    customCta: "Demander un devis",
    whyEyebrow: "Pourquoi DOC TOGO",
    whyTitle: "L'administration togolaise, sans la file d'attente.",
    whySub: "Nous combinons connaissance du terrain, rigueur administrative et outils digitaux pour transformer vos démarches en une expérience simple et suivie.",
    whyBadgeLabel: "pays desservis pour la diaspora togolaise",
    whyImgAlt: "Accompagnement entrepreneurial au Togo",
    testiEyebrow: "Témoignages",
    testiTitle: "Ils nous ont confié leurs démarches.",
    testiSub: "De Lomé à Paris, de Bruxelles à New York : la confiance de nos clients est notre meilleure référence.",
    faqEyebrow: "FAQ",
    faqTitle: "Vos questions, nos réponses.",
    faqSub: "Délais, tarifs, sécurité, suivi : tout ce qu'il faut savoir avant de démarrer votre démarche.",
    faqAll: "Consulter toute la FAQ",
    heroImg1: "Lomé, Togo",
    heroImg2: "Documents de voyage",
  },
  en: {
    badge: "Need to complete a procedure in Togo?",
    sub1: "Whether you are in Togo or abroad, DOC TOGO supports you with administrative procedures, company formation and your relocation projects in Togo.",
    sub2: "One need. One process. One partner.",
    disclaimer: <>DOC TOGO is a private assistance platform: <strong className="font-semibold text-white/80">we are not the Togolese administration and we do not replace it.</strong> We provide the information and assistance the applicant needs, from filing to document delivery.</>,
    stats: [
      { value: "99.4%", label: "of cases completed" },
      { value: "1,500+", label: "procedures supported" },
      { value: "24h", label: "response time" },
    ],
    trust1: "Diaspora & investors",
    trust2: "trust us in 20+ countries",
    servicesEyebrow: "Our services",
    servicesSub: "Five areas of expertise for individuals, the diaspora, foreign nationals and companies.",
    allServices: "See all our services",
    custom: "Tailor-made",
    customTitle: "Can't find your procedure?",
    customText: "Tell us what you need: we review every request, even the most specific ones.",
    customCta: "Request a quote",
    whyEyebrow: "Why DOC TOGO",
    whyTitle: "Togolese administration, without the queue.",
    whySub: "We combine field knowledge, administrative rigour and digital tools to turn your procedures into a simple, tracked experience.",
    whyBadgeLabel: "countries served for the Togolese diaspora",
    whyImgAlt: "Business support in Togo",
    testiEyebrow: "Testimonials",
    testiTitle: "They trusted us with their procedures.",
    testiSub: "From Lomé to Paris, Brussels to New York: our clients' trust is our best reference.",
    faqEyebrow: "FAQ",
    faqTitle: "Your questions, our answers.",
    faqSub: "Timelines, pricing, security, tracking: everything you need to know before starting your request.",
    faqAll: "Browse the full FAQ",
    heroImg1: "Lomé, Togo",
    heroImg2: "Travel documents",
  },
};

const Hero = () => {
  const ref = useRef(null);
  const { lang, t } = useLang();
  const { MARQUEE_ITEMS } = useContent();
  const c = COPY[lang];
  const { scrollYProgress } = useScroll({ target: ref, offset: ["start start", "end start"] });
  const yMain = useTransform(scrollYProgress, [0, 1], [0, 90]);
  const ySecond = useTransform(scrollYProgress, [0, 1], [0, -70]);

  const lines = lang === "en"
    ? [<>Your procedures in <span className="text-gold">Togo</span>,</>, <>made <span className="relative inline-block">simple.<span className="absolute -bottom-1 left-0 h-1.5 w-full rounded-full bg-gradient-to-r from-gold via-[#FFCE00] to-crimson" /></span></>]
    : [<>Vos démarches au <span className="text-gold">Togo</span>,</>, <>en toute <span className="relative inline-block">simplicité.<span className="absolute -bottom-1 left-0 h-1.5 w-full rounded-full bg-gradient-to-r from-gold via-[#FFCE00] to-crimson" /></span></>];

  return (
    <section ref={ref} data-testid="hero-section" className="relative overflow-hidden bg-brand-deep text-white">
      <div className="pointer-events-none absolute -top-40 right-[-8%] h-[520px] w-[520px] rounded-full bg-gold/15 blur-[130px]" />
      <div className="pointer-events-none absolute bottom-[-10%] left-[-8%] h-[420px] w-[420px] rounded-full bg-brand-muted/40 blur-[110px]" />
      <div className="relative mx-auto grid max-w-7xl grid-cols-1 items-center gap-14 px-6 pt-32 pb-16 lg:grid-cols-12 lg:pt-44 lg:pb-24">
        <div className="lg:col-span-7">
          <motion.div
            initial={{ opacity: 0, y: 16 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.7, ease: [0.22, 1, 0.36, 1] }}
            data-testid="hero-badge"
            className="mb-7 inline-flex items-center gap-2.5 rounded-full border border-white/15 bg-white/5 px-4 py-2 backdrop-blur-md"
          >
            <FlagDot />
            <span className="font-mono text-[11px] font-bold uppercase tracking-[0.25em] text-white/80">
              {c.badge}
            </span>
          </motion.div>
          <MaskedLines
            key={lang}
            delay={0.15}
            className="font-display text-4xl sm:text-5xl lg:text-6xl font-extrabold tracking-tight leading-[1.06] text-balance"
            lines={lines}
          />
          <motion.p
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.8, delay: 0.55, ease: [0.22, 1, 0.36, 1] }}
            className="mt-7 max-w-xl text-base sm:text-lg leading-relaxed text-white/60"
          >
            {c.sub1}
            <span className="mt-2 block font-semibold text-white/85">{c.sub2}</span>
          </motion.p>
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.8, delay: 0.7, ease: [0.22, 1, 0.36, 1] }}
            className="mt-9 flex flex-col gap-4 sm:flex-row"
          >
            <Link
              to="/contact"
              data-testid="hero-demarrer-btn"
              className="group inline-flex items-center justify-center gap-2 rounded-full bg-gold px-8 py-4 font-display text-base font-bold text-brand-deep shadow-lg shadow-gold/25 transition-[background-color,transform] duration-300 hover:bg-gold-warm hover:-translate-y-0.5"
            >
              {t("cta.start")}
              <ArrowRight className="h-5 w-5 transition-transform duration-300 group-hover:translate-x-1" />
            </Link>
            <a
              href={WHATSAPP_URL}
              target="_blank"
              rel="noopener noreferrer"
              data-testid="hero-whatsapp-btn"
              className="inline-flex items-center justify-center gap-2 rounded-full border border-white/25 px-8 py-4 font-display text-base font-bold text-white transition-[background-color,border-color] duration-300 hover:bg-white/10 hover:border-[#25D366]/60"
            >
              <MessageCircle className="h-5 w-5 text-[#25D366]" />
              {t("cta.whatsapp")}
            </a>
          </motion.div>
          <motion.div
            initial={{ opacity: 0, y: 16 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.8, delay: 0.8, ease: [0.22, 1, 0.36, 1] }}
            data-testid="hero-disclaimer"
            className="mt-6 flex max-w-xl items-start gap-3 rounded-2xl border border-white/10 bg-white/5 px-5 py-4 backdrop-blur-md"
          >
            <Info className="mt-0.5 h-4 w-4 shrink-0 text-gold" />
            <p className="text-xs leading-relaxed text-white/55">{c.disclaimer}</p>
          </motion.div>
          <motion.div
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            transition={{ duration: 1, delay: 0.9 }}
            className="mt-12 grid max-w-lg grid-cols-3 gap-6 border-t border-white/10 pt-8"
          >
            {c.stats.map((s) => (
              <div key={s.label} data-testid={`hero-stat-${s.label.split(" ").pop()}`}>
                <div className="font-display text-2xl sm:text-3xl font-extrabold text-gold">{s.value}</div>
                <div className="mt-1 text-xs leading-snug text-white/50">{s.label}</div>
              </div>
            ))}
          </motion.div>
        </div>
        <div className="relative hidden h-[560px] lg:col-span-5 lg:block">
          <motion.div style={{ y: yMain }} className="absolute inset-x-10 top-0">
            <motion.div
              initial={{ opacity: 0, scale: 0.94, rotate: 4 }}
              animate={{ opacity: 1, scale: 1, rotate: 2 }}
              transition={{ duration: 1.1, delay: 0.4, ease: [0.22, 1, 0.36, 1] }}
            >
              <img
                src={IMAGES.hero}
                alt={c.heroImg1}
                className="h-[430px] w-full rounded-[2rem] object-cover shadow-2xl shadow-black/50"
              />
            </motion.div>
          </motion.div>
          <motion.div style={{ y: ySecond }} className="absolute -left-2 bottom-6">
            <motion.img
              initial={{ opacity: 0, y: 40, rotate: -8 }}
              animate={{ opacity: 1, y: 0, rotate: -6 }}
              transition={{ duration: 1, delay: 0.65, ease: [0.22, 1, 0.36, 1] }}
              src={IMAGES.heroSecondary}
              alt={c.heroImg2}
              className="h-60 w-52 rounded-3xl border-4 border-brand-deep object-cover shadow-2xl"
            />
          </motion.div>
          <motion.div
            initial={{ opacity: 0, y: 30 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.9, delay: 0.85, ease: [0.22, 1, 0.36, 1] }}
            data-testid="hero-trust-card"
            className="absolute right-0 bottom-16 rounded-2xl border border-white/15 bg-white/10 p-5 backdrop-blur-xl shadow-2xl"
          >
            <div className="flex items-center gap-1.5">
              {[...Array(5)].map((_, i) => (
                <Star key={i} className="h-4 w-4 fill-gold text-gold" />
              ))}
            </div>
            <p className="mt-2 text-sm font-semibold text-white">{c.trust1}</p>
            <p className="text-xs text-white/55">{c.trust2}</p>
          </motion.div>
        </div>
      </div>
      <Marquee items={MARQUEE_ITEMS} />
    </section>
  );
};

const ServicesPreview = () => {
  const { lang } = useLang();
  const { SERVICES } = useContent();
  const c = COPY[lang];
  return (
  <section data-testid="services-preview" className="bg-ivory py-24 sm:py-28">
    <div className="mx-auto max-w-7xl px-6">
      <div className="mb-14 flex flex-col gap-8 lg:flex-row lg:items-end lg:justify-between">
        <SectionHeading
          eyebrow={c.servicesEyebrow}
          title={lang === "en" ? <>All your procedures,<br />one single platform.</> : <>Toutes vos démarches,<br />une seule plateforme.</>}
          sub={c.servicesSub}
        />
        <Reveal delay={0.15}>
          <Link
            to="/services"
            data-testid="services-preview-all-link"
            className="group inline-flex items-center gap-2 font-mono text-xs font-bold uppercase tracking-[0.25em] text-brand"
          >
            {c.allServices}
            <ArrowRight className="h-4 w-4 transition-transform duration-300 group-hover:translate-x-1" />
          </Link>
        </Reveal>
      </div>
      <div className="grid grid-cols-1 gap-6 md:grid-cols-2 lg:grid-cols-3">
        {SERVICES.map((s, i) => (
          <Reveal key={s.id} delay={i * 0.08} className={i === 0 ? "md:col-span-2 lg:col-span-1" : ""}>
            <Link
              to={s.link || "/services"}
              data-testid={`service-card-${s.id}`}
              className="group flex h-full flex-col rounded-3xl border border-brand/10 bg-white p-8 shadow-sm transition-[transform,box-shadow,border-color] duration-300 hover:-translate-y-1.5 hover:border-gold/40 hover:shadow-xl hover:shadow-brand/10"
            >
              <div className="mb-6 flex items-center justify-between">
                <span className="grid h-12 w-12 place-items-center rounded-2xl bg-brand-light text-brand transition-colors duration-300 group-hover:bg-brand group-hover:text-white">
                  <s.icon className="h-6 w-6" />
                </span>
                <ArrowUpRight className="h-5 w-5 text-slate-300 transition-[color,transform] duration-300 group-hover:translate-x-0.5 group-hover:-translate-y-0.5 group-hover:text-gold" />
              </div>
              <h3 className="font-display text-xl font-bold text-slate-900">{s.title}</h3>
              <p className="mt-2.5 flex-1 text-sm leading-relaxed text-slate-600">{s.short}</p>
              <ul className="mt-5 space-y-2 border-t border-slate-100 pt-5">
                {s.items.slice(0, 4).map((item) => (
                  <li key={item} className="flex items-start gap-2 text-xs font-medium text-slate-500">
                    <Check className="mt-0.5 h-3.5 w-3.5 shrink-0 text-brand" /> {item}
                  </li>
                ))}
              </ul>
            </Link>
          </Reveal>
        ))}
        <Reveal delay={0.4}>
          <Link
            to="/contact"
            data-testid="service-card-cta"
            className="group relative flex h-full min-h-[280px] flex-col justify-between overflow-hidden rounded-3xl bg-brand p-8 text-white transition-transform duration-300 hover:-translate-y-1.5"
          >
            <div className="pointer-events-none absolute -right-16 -top-16 h-48 w-48 rounded-full bg-gold/25 blur-2xl" />
            <div>
              <span className="font-mono text-xs font-bold uppercase tracking-[0.3em] text-gold">{c.custom}</span>
              <h3 className="mt-3 font-display text-2xl font-bold leading-snug">{c.customTitle}</h3>
              <p className="mt-3 text-sm leading-relaxed text-white/65">{c.customText}</p>
            </div>
            <span className="mt-8 inline-flex items-center gap-2 font-display font-bold text-gold">
              {c.customCta}
              <ArrowRight className="h-5 w-5 transition-transform duration-300 group-hover:translate-x-1" />
            </span>
          </Link>
        </Reveal>
      </div>
    </div>
  </section>
  );
};

const WhySection = () => {
  const { lang } = useLang();
  const { WHY_POINTS } = useContent();
  const c = COPY[lang];
  return (
  <section data-testid="why-section" className="relative overflow-hidden bg-white py-24 sm:py-28">
    <div className="mx-auto grid max-w-7xl grid-cols-1 items-center gap-16 px-6 lg:grid-cols-2">
      <div>
        <SectionHeading eyebrow={c.whyEyebrow} title={c.whyTitle} sub={c.whySub} />
        <div className="mt-10 space-y-8">
          {WHY_POINTS.map((p, i) => (
            <Reveal key={p.title} delay={i * 0.1}>
              <div data-testid={`why-point-${i}`} className="flex gap-5">
                <span className="grid h-12 w-12 shrink-0 place-items-center rounded-2xl bg-gold-glow text-gold">
                  <p.icon className="h-6 w-6" />
                </span>
                <div>
                  <h3 className="font-display text-lg font-bold text-slate-900">{p.title}</h3>
                  <p className="mt-1.5 text-sm leading-relaxed text-slate-600">{p.desc}</p>
                </div>
              </div>
            </Reveal>
          ))}
        </div>
      </div>
      <Reveal delay={0.2} className="relative">
        <div className="absolute -inset-4 rounded-[2.5rem] border border-brand/15 -rotate-1" />
        <img
          src={IMAGES.business}
          alt={c.whyImgAlt}
          loading="lazy"
          className="relative h-[420px] sm:h-[520px] w-full rounded-[2rem] object-cover shadow-2xl"
        />
        <div className="absolute -bottom-6 -left-6 rounded-2xl bg-brand-deep p-6 text-white shadow-2xl">
          <div className="font-display text-3xl font-extrabold text-gold">20+</div>
          <div className="mt-1 text-xs text-white/60">{c.whyBadgeLabel}</div>
        </div>
      </Reveal>
    </div>
  </section>
  );
};

const Testimonials = () => {
  const { lang } = useLang();
  const { TESTIMONIALS } = useContent();
  const c = COPY[lang];
  return (
  <section data-testid="testimonials-section" className="bg-ivory py-24 sm:py-28">
    <div className="mx-auto max-w-7xl px-6">
      <SectionHeading align="center" eyebrow={c.testiEyebrow} title={c.testiTitle} sub={c.testiSub} />
      <div className="mt-14 grid grid-cols-1 gap-6 md:grid-cols-3">
        {TESTIMONIALS.map((t, i) => (
          <Reveal key={t.name} delay={i * 0.1}>
            <figure
              data-testid={`testimonial-${i}`}
              className="flex h-full flex-col rounded-3xl border border-brand/10 bg-white p-8 shadow-sm transition-[transform,box-shadow] duration-300 hover:-translate-y-1 hover:shadow-lg"
            >
              <Quote className="h-8 w-8 text-gold/50" />
              <blockquote className="mt-4 flex-1 text-sm leading-relaxed text-slate-700">« {t.text} »</blockquote>
              <figcaption className="mt-6 border-t border-slate-100 pt-5">
                <div className="mb-2 flex gap-1">
                  {[...Array(5)].map((_, j) => (
                    <Star key={j} className="h-3.5 w-3.5 fill-gold text-gold" />
                  ))}
                </div>
                <div className="font-display font-bold text-slate-900">{t.name}</div>
                <div className="text-xs text-slate-500">{t.role}</div>
              </figcaption>
            </figure>
          </Reveal>
        ))}
      </div>
    </div>
  </section>
  );
};

const FaqPreview = () => {
  const { lang } = useLang();
  const { FAQ_ITEMS } = useContent();
  const c = COPY[lang];
  return (
  <section data-testid="faq-preview" className="bg-white py-24 sm:py-28">
    <div className="mx-auto grid max-w-7xl grid-cols-1 gap-14 px-6 lg:grid-cols-12">
      <div className="lg:col-span-5">
        <SectionHeading eyebrow={c.faqEyebrow} title={c.faqTitle} sub={c.faqSub} />
        <Reveal delay={0.15}>
          <Link
            to="/faq"
            data-testid="faq-preview-all-link"
            className="group mt-8 inline-flex items-center gap-2 font-mono text-xs font-bold uppercase tracking-[0.25em] text-brand"
          >
            {c.faqAll}
            <ArrowRight className="h-4 w-4 transition-transform duration-300 group-hover:translate-x-1" />
          </Link>
        </Reveal>
      </div>
      <Reveal delay={0.1} className="lg:col-span-7">
        <Accordion type="single" collapsible className="w-full" data-testid="faq-preview-accordion">
          {FAQ_ITEMS.slice(0, 4).map((item, i) => (
            <AccordionItem key={i} value={`item-${i}`} className="border-brand/10">
              <AccordionTrigger data-testid={`faq-preview-q-${i}`} className="text-left font-display text-base font-bold text-slate-900 hover:text-brand hover:no-underline">
                {item.q}
              </AccordionTrigger>
              <AccordionContent className="text-sm leading-relaxed text-slate-600">{item.a}</AccordionContent>
            </AccordionItem>
          ))}
        </Accordion>
      </Reveal>
    </div>
  </section>
  );
};

export default function Home() {
  usePageTitle(null);
  return (
    <div data-testid="home-page">
      <ConcoursBanner />
      <Hero />
      <ServicesPreview />
      <WhySection />
      <ProcessSection />
      <AppCta />
      <Testimonials />
      <FaqPreview />
      <CTABand />
    </div>
  );
}
